import { Box, Flex } from '@chakra-ui/react'
import OmensCookieConsent from 'components/common/CookieConsent'
import { LandingContainer } from 'components/common/LandingContainer'
import MedleveAboutSection from 'components/sections/MedleveAboutSection'
import { TestimoniesSection } from 'components/sections/TestimoniesSection'
import TypeformPreloader from 'components/sections/TypeformPreloader'
import WeightTestimoniesSection from 'components/sections/WeightTestimoniesSection'
import { Body1, H3 } from 'components/typography'
import Head from 'next/head'
import { FC, useEffect } from 'react'
import LazyLoad from 'react-lazyload'
import { setCookies } from 'utils/cookieHelpers'
import { medleveVideoTestimonies } from 'utils/data/testimonies'

import { FaqAccordion } from '../../medleveSeo/components/FaqAccordion'
import { MedleveSeoHead } from '../../medleveSeo/components/MedleveSeoHead'
import { buildPageGraph } from '../../medleveSeo/jsonLd'
import { BRANDS, getEntryByPath, HOME_PATH, MOLECULES } from '../../medleveSeo/registry'
import HomeFooter from './components/HomeFooter'
import HomeHeader from './components/HomeHeader'
import HomeHero from './components/HomeHero'
import HomeHowItWorks from './components/HomeHowItWorks'
import HomeMedications from './components/HomeMedications'
import HomeSection from './components/HomeSection'
import HomeStickyCta from './components/HomeStickyCta'
import { HOME_ABOUT_ID, HOME_FACEBOOK_DOMAIN_VERIFICATION } from './constants'
import {
  HOME_ABOUT,
  HOME_DISCLAIMER,
  HOME_FAQ,
  HOME_FAQ_TITLE,
  HOME_FOLLOW_UP,
  HOME_GLP1,
  HOME_META_DESCRIPTION,
  HOME_TITLE,
  HOME_TREATMENTS,
  HOME_VIDEO_CONSULTATION,
  HOME_VIDEO_TESTIMONIES_TITLE
} from './content'

/**
 * Entity graph of the homepage (PRD §39–42, §51–54): Organization + WebSite + WebPage + FAQPage, with
 * the molecules and brands that the visible copy really names as `mentions`.
 * - FAQPage is built from the SAME `HOME_FAQ` array the accordion renders.
 * - `dateModified` comes from the registry entry of the homepage (a hand-maintained date, never build time).
 * Module-level on purpose: it is static, so server and client serialize the exact same string.
 */
const HOME_JSON_LD = buildPageGraph({
  page: {
    path: HOME_PATH,
    name: HOME_TITLE,
    description: HOME_META_DESCRIPTION,
    dateModified: getEntryByPath(HOME_PATH)?.review.dateModified
  },
  about: 'organization',
  mentions: [...MOLECULES, ...BRANDS],
  faq: HOME_FAQ
})

/**
 * ORGANIC homepage of medleve.com (`/` on the Medleve host only) — SEO + GEO PRD §4, §6–8, §11–21,
 * §30–38, §70–71, §75–78, §83.
 *
 * Isolation: this tree is new and rendered only by `pages/index.tsx` for the Medleve host. The
 * paid/campaign routes (`/gads*`, `/meta`, `/medleve`, `/campanha-*`) keep rendering `MedLevePage` and
 * its sections, none of which is modified. Shared components are imported read-only; whatever needed a
 * change (header, hero, benefits, how-it-works, sticky CTA, footer) was copied into `./components`.
 *
 * Server rendering: every heading, paragraph, FAQ answer and the `<head>` metadata below render without
 * cookies, `window`, breakpoints-in-JS or persisted redux state, so the server HTML carries the full
 * content and hydrates without mismatch. (`_app` skips its `PersistGate` for pages flagged with the
 * `medleveOrganic` page prop — see `pages/index.tsx` — which is what lets that HTML be emitted.)
 * Only the interactive trust widgets of the current landing (patient videos, doctors/press, quote
 * carousel) stay behind `react-lazyload`, exactly as today.
 *
 * Tracking: nothing is added or removed. GTM/Insider come from `_app`; `OmensCookieConsent` (which fires
 * the OpenAI pixel page view after consent) and `TypeformPreloader` are mounted as in the current landing,
 * and the `facebook-domain-verification` meta the previous homepage carried is emitted with the same value.
 */
const MedLeveHomePage: FC<{ preview?: boolean }> = ({ preview = false }) => {
  // Same brand cookie the current landing sets on mount (drives brand detection in the rest of the app).
  useEffect(() => {
    setCookies({ name: 'isMedleveAddress', value: 'true' })
  }, [])

  return (
    <>
      {/* `preview` (review-only URL) keeps everything identical to the real homepage except `noindex, nofollow`. */}
      <MedleveSeoHead
        title={HOME_TITLE}
        description={HOME_META_DESCRIPTION}
        path={HOME_PATH}
        jsonLd={HOME_JSON_LD}
        index={!preview}
      />
      {/* Meta (Facebook) domain verification: the previous homepage emitted it through `CharlesMeta`. */}
      <Head>
        <meta
          key="facebook-domain-verification"
          name="facebook-domain-verification"
          content={HOME_FACEBOOK_DOMAIN_VERIFICATION}
        />
      </Head>

      <OmensCookieConsent />
      <HomeHeader />

      <Flex as="main" direction="column" overflowX="hidden">
        <HomeHero />
        <HomeHowItWorks />

        {/* REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW — PRD §7/§15 ("em poucos minutos"; never "< 5 minutos"). */}
        <HomeSection
          id="consulta-por-video"
          title={HOME_VIDEO_CONSULTATION.title}
          paragraphs={[HOME_VIDEO_CONSULTATION.body]}
        >
          <Flex as="ul" listStyleType="none" m={0} p={0} direction={{ base: 'column', md: 'row' }} gap={4}>
            {HOME_VIDEO_CONSULTATION.modalities.map(({ title, description }) => (
              <Flex
                as="li"
                key={title}
                flex={1}
                bg="white"
                borderRadius={{ base: '24px', lg: '32px' }}
                direction="column"
                p={6}
                gap={2}
              >
                <H3
                  fontWeight={800}
                  fontSize={{ base: '1rem', lg: '1.5rem' }}
                  letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
                  lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
                >
                  {title}
                </H3>
                <Body1>{description}</Body1>
              </Flex>
            ))}
          </Flex>
        </HomeSection>

        {/* Trust element of the current landing (patient videos), unchanged and still lazy. */}
        <LazyLoad once unmountIfInvisible>
          <TestimoniesSection
            title={HOME_VIDEO_TESTIMONIES_TITLE}
            testimonies={medleveVideoTestimonies}
            variant="medleve"
          />
        </LazyLoad>

        {/* REQUIRES MEDICAL REVIEW — PRD §16. */}
        <HomeSection
          id="tratamentos"
          tone="muted"
          title={HOME_TREATMENTS.title}
          paragraphs={HOME_TREATMENTS.paragraphs}
        />

        {/* REQUIRES MEDICAL REVIEW — PRD §18. */}
        <HomeSection id="glp-1" tone="brand" title={HOME_GLP1.title} paragraphs={HOME_GLP1.paragraphs} />

        {/* REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW — PRD §17 (+ §4, §6). */}
        <HomeMedications />

        {/* REQUIRES MEDICAL REVIEW — PRD §19. */}
        <HomeSection
          id="acompanhamento-medico"
          tone="muted"
          title={HOME_FOLLOW_UP.title}
          paragraphs={HOME_FOLLOW_UP.paragraphs}
        />

        {/*
          Trust elements of the current landing, unchanged and still lazy.
          REQUIRES LEGAL REVIEW — `MedleveAboutSection` carries the "+40 mil brasileiros" figure and the
          doctors' credentials (CRM); `WeightTestimoniesSection` carries patient quotes. They are shared
          with the paid landings and were NOT edited here.
        */}
        <LazyLoad once unmountIfInvisible>
          <MedleveAboutSection />
        </LazyLoad>
        <LazyLoad once unmountIfInvisible>
          <WeightTestimoniesSection medleve />
        </LazyLoad>

        {/* REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW — PRD §20; same array as the FAQPage JSON-LD. */}
        <Box bg="alabaster">
          <LandingContainer py={{ base: 8, lg: 32 }}>
            <FaqAccordion
              id="perguntas-frequentes"
              aria-label={HOME_FAQ_TITLE}
              title={HOME_FAQ_TITLE}
              items={HOME_FAQ}
              scrollMarginTop="88px"
            />
          </LandingContainer>
        </Box>

        {/* REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW — PRD §21. */}
        <HomeSection id={HOME_ABOUT_ID} tone="muted" title={HOME_ABOUT.title} paragraphs={HOME_ABOUT.paragraphs} />

        {/* REQUIRES MEDICAL REVIEW — disclaimer of the current landing. */}
        <Flex align="center" justify="center" py={4} px={6} bg="alabaster" color="phthalo" textAlign="center">
          <Body1>{HOME_DISCLAIMER}</Body1>
        </Flex>
      </Flex>

      <HomeFooter />

      <HomeStickyCta />
      <TypeformPreloader typeformId={process.env.NEXT_PUBLIC_TYPEFORM_ID_PRE_ANALYSIS_MEDLEVE} />
    </>
  )
}

export default MedLeveHomePage
